import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FindDocumentById } from 'src/graphql/documents'
import 'src/components/general/js/forms/reconcile/style.scss'
import { Button, Form, Icon, Popup, Input, Checkbox } from 'semantic-ui-react'
import { formatMoneyWithCurrency, formatRut } from 'src/utils/formatter'
import { formatBanks } from 'src/utils/bankList'
import LoadingSpinner from 'src/components/general/js/spinnerLoading'
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment'
import FeatherIcon from 'feather-icons-react'
import DocumentDetailContainer from 'src/components/documentostributarios/DocumentDetailContainer'
import { useDocumentModal } from 'src/utils/documentModal'
import { useHistory, useLocation } from 'react-router-dom'
import { useCashPositionLabels } from 'src/utils/cashPositionLabels'
import './CashPositionDetail.scss'

const top = -30;

const CashPositionDetail = ({ editData }) => {
  const { t } = useTranslation('dashboard')
  const { category: categoryLabel, field: fieldLabel } = useCashPositionLabels()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { openDocumentDetail } = useDocumentModal(history, location)
  
  const { data, fetching, reexecuteQuery } = FindDocumentById({ ids: editData?.documents || [] })

  const [activeFilter, setActiveFilter] = useState(null);
  const [isCleared, setIsCleared] = useState(false);

  const [filters, setFilters] = useState({
    tipo: [],
    montoMin: '',
    montoMax: '',
    folio: '',
  });

  const [displayValues, setDisplayValues] = useState({ montoMin: "", montoMax: "" });

  const [filteredData, setFilteredData] = useState([]);
  const filterRef = useRef(null);

  useEffect(() => {
    if (data?.findDocumentsById) {
      setFilteredData(data.findDocumentsById);
    } else {
      setFilteredData([]);
    }
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setActiveFilter(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleModalData = (document) => {
    if (!document) return
    openDocumentDetail(document.id)
  }

  const filterOptions = [
    { id: 'type', label: t('cashPosition.detailFilters.type') },
    { id: 'amount', label: t('cashPosition.detailFilters.amount') },
    { id: 'folio', label: t('cashPosition.detailFilters.folio') },
  ];

  const applyFilters = () => {
    const documentsToFilter = data?.findDocumentsById || [];

    const filtered = documentsToFilter.filter((doc) => {

      const matchesTipo = filters.tipo.length
        ? doc.documentType === 'CREDIT'
          ? filters.tipo.includes(`Cuota ${doc.meta?.paymentNumber}`)
          : filters.tipo.includes(doc.meta?.summary || doc.meta?.typeLabel)
        : true

      const matchesMonto =
        (!filters.montoMin || Math.abs(doc.meta?.remaining || doc.meta?.netAmount || doc?.meta?.originalAmount) >= Number(filters.montoMin)) &&
        (!filters.montoMax || Math.abs(doc.meta?.remaining || doc.meta?.netAmount || doc?.meta?.originalAmount) <= Number(filters.montoMax));

      const matchesFolio = filters.folio
        ? filters.folio
          .split(';')
          .map((f) => f.trim())
          .includes(String(doc.meta?.documentId || doc.meta?.number))
        : true;

      return matchesTipo && matchesMonto && matchesFolio;
    });

    setActiveFilter(false);
    setFilteredData(filtered);
  };

  const clearFilters = (filter) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev };

      if (filter === 'tipo') {
        updatedFilters.tipo = [];
      } else if (filter === 'monto') {
        updatedFilters.montoMin = '';
        updatedFilters.montoMax = '';
        setDisplayValues({ montoMin: "", montoMax: "" });
      } else if (filter === 'folio') {
        updatedFilters.folio = '';
      }
      setIsCleared(true);

      return updatedFilters;
    });
  };

  useEffect(() => {
    if (isCleared) {
      applyFilters();
      setIsCleared(false);
    }
  }, [filters, isCleared]);


  const handleInputChange = (key) => (e) => {
    const rawValue = e.target.value.replace(/[^\d]/g, "");
    setFilters((prev) => ({ ...prev, [key]: rawValue }));
    setDisplayValues((prev) => ({ ...prev, [key]: formatMoneyWithCurrency(rawValue) }));
  };

  const handleBlur = (key) => () => {
    setDisplayValues((prev) => ({
      ...prev,
      [key]: formatMoneyWithCurrency(filters[key]),
    }));
  };

  const handleFocus = (key) => () => {
    setDisplayValues((prev) => ({
      ...prev,
      [key]: filters[key],
    }));
  };

  // Traducir categoryKey a nombre legible
  const getCategoryLabel = (categoryKey) => categoryLabel(categoryKey);
  const getFieldLabel = (fieldKey) => fieldLabel(fieldKey);

  return (
    <>
      <div className='w-100'>
        <div className="d-flex flex-column w-100 reconcileDocumentInformation">
          <div className="w-100 justify-content-start reconcileDocumentData">
            <span className='cashPositionFieldTitle'>
              {editData?.fieldLabel || getFieldLabel(editData?.fieldKey) || editData?.categoryLabel || getCategoryLabel(editData?.categoryKey)}
            </span>
            <span className='cashPositionCategoryLabel'>
              {editData?.categoryLabel || getCategoryLabel(editData?.categoryKey)}
            </span>
            <span className='cashPositionPeriodLabel'>
              {editData?.periodLabel || t('cashPosition.detailDefaultTitle')}
            </span>
          </div>
          <div className="tw-pt-[12px] tw-flex tw-gap-[8px]">
            {filterOptions.map((option, index) => {
              const isFilterActive =
                option.id === 'type' && filters.tipo.length > 0 ||
                option.id === 'amount' && (filters.montoMin || filters.montoMax) ||
                option.id === 'folio' && filters.folio;

              return (
                <div
                  key={index}
                  className={`tw-flex tw-items-center tw-justify-between tw-w-[80px] tw-h-[35px] tw-border-solid tw-border-[1px] tw-rounded-[4px] tw-px-[10px] tw-cursor-pointer ${isFilterActive ? 'tw-border-purple-600 tw-text-purple-600 tw-border-[2px] tw-font-semibold' : 'tw-border-gray-500 tw-bg-white'}`}
                  onClick={() => setActiveFilter(activeFilter === option.id ? null : option.id)}
                >
                  <p className={`tw-pt-[13px]`}>{option.label}</p>
                  <FeatherIcon
                    icon="chevron-down"
                    size="15px"
                    strokeWidth={`${isFilterActive ? '3px' : '2px'}`}
                  />
                </div>
              );
            })}
          </div>
          {activeFilter && (
            <div ref={filterRef} className="tw-fixed tw-top-[190px] tw-mt-[2px] tw-w-[400px] tw-h-[250px] tw-px-[20px] tw-py-[12px] tw-border-solid tw-border-[1px] tw-bg-white tw-border-gray-300 tw-rounded-[4px] tw-z-50 tw-flex tw-flex-col">
              {activeFilter === 'type' && (
                <div className="tw-grid">
                  <div className='tw-flex tw-justify-between'>
                    <p className="tw-text-[16px] tw-font-sans tw-font-bold">Filtrar por Tipo</p>
                    <FeatherIcon icon='x' size={'20px'} strokeWidth={'1.5px'} className='tw-cursor-pointer' onClick={() => setActiveFilter(false)} />
                  </div>
                  {[...new Set((data?.findDocumentsById || []).map((doc) => doc.meta?.summary || doc.meta?.typeLabel || doc.meta?.paymentNumber && `Cuota ${doc.meta?.paymentNumber}`).filter(Boolean))].map(
                    (summary) => (
                      <div key={summary} className="tw-mb-[8px]">
                        <Checkbox
                          label={summary}
                          checked={filters.tipo.includes(summary)}
                          onChange={(e, { checked }) => {
                            setFilters((prev) => {
                              const updatedTypes = checked
                                ? [...prev.tipo, summary]
                                : prev.tipo.filter((type) => type !== summary);
                              return { ...prev, tipo: updatedTypes };
                            });
                          }}
                        />
                      </div>
                    )
                  )}
                </div>
              )}

              {activeFilter === 'amount' && (
                <>
                  <div className='tw-flex tw-justify-between'>
                    <p className="tw-text-[16px] tw-font-sans tw-font-bold">Filtrar por Monto</p>
                    <FeatherIcon icon='x' size={'20px'} strokeWidth={'1.5px'} className='tw-cursor-pointer' onClick={() => setActiveFilter(false)} />
                  </div>
                  <div>
                    <label className="tw-text-xs">Desde</label>
                    <Input
                      type="text"
                      className="tw-border tw-rounded tw-w-full"
                      value={displayValues.montoMin}
                      onChange={handleInputChange("montoMin")}
                      onBlur={handleBlur("montoMin")}
                      onFocus={handleFocus("montoMin")}
                    />
                  </div>
                  <div className="tw-pt-[8px]">
                    <label className="tw-text-xs">Hasta</label>
                    <Input
                      type="text"
                      className="tw-border tw-rounded tw-w-full"
                      value={displayValues.montoMax}
                      onChange={handleInputChange("montoMax")}
                      onBlur={handleBlur("montoMax")}
                      onFocus={handleFocus("montoMax")}
                    />
                  </div>
                </>
              )}

              {activeFilter === 'folio' && (
                <>
                  <div className='tw-flex tw-justify-between'>
                    <p className="tw-text-[16px] tw-font-sans tw-font-bold">Filtrar por Folio</p>
                    <FeatherIcon icon='x' size={'20px'} strokeWidth={'1.5px'} className='tw-cursor-pointer' onClick={() => setActiveFilter(false)} />
                  </div>
                  <div>
                    <Input
                      type="text"
                      className="tw-border tw-rounded tw-w-full"
                      value={filters.folio}
                      onChange={(e) => setFilters((prev) => ({ ...prev, folio: e.target.value }))}
                    />
                  </div>
                  <div className="tw-w-full tw-h-[88px] tw-bg-gray-50 tw-p-[14px] tw-flex tw-gap-x-[6px] tw-mt-[12px] tw-rounded-md">
                    <div className="tw-w-1/4">
                      <FeatherIcon icon="info" size={'24px'} strokeWidth={'1.5px'} />
                    </div>
                    <p className="tw-text-[14px]">
                      Puedes buscar por múltiples folios separando estos por Punto y Coma (;). Ejemplo: 1234;7654
                    </p>
                  </div>
                </>
              )}

              <div className="tw-flex tw-items-end tw-mt-auto tw-gap-x-[12px]">
                <Button
                  type="button"
                  onClick={applyFilters}
                  color='blue'
                >
                  Aplicar filtros
                </Button>
                <Button type="button" onClick={() => clearFilters(activeFilter.toLowerCase())}>
                  Limpiar
                </Button>
              </div>
            </div>
          )}
        </div>
        <div
          className="d-flex flex-column p-2 mt-4 reconcileTableScroll"
          style={{ maxHeight: '70vh' }}
        >
          <div className="d-flex flex-column reconcileSearchTable">
            {data && !fetching
              ? (filteredData.length > 0 ? filteredData.map((el, index) => {
                const isReceived = el.meta?.isReceived || el.documentType == "CREDIT"
                let documentType, documentId, rut, name, amount, description

                // Usar el amount del documento (nivel superior) como fuente principal
                // Este es el monto total correcto del documento
                const documentAmount = el.amount || el.meta?.amount || el.meta?.originalAmount || 0

                if (['OBLIGATION'].includes(el.documentType)) {
                  documentType = el.meta.typeLabel
                  documentId = el.meta.number && `#${el.meta.number}`
                  rut = (isReceived ? formatRut(el.meta.issuerId) : formatRut(el.meta.receiverId)) || '-'
                  name = isReceived ? el.meta.issuerName : el.meta.receiverName
                  amount = `${formatMoneyWithCurrency(el.meta.remaining || el.meta.originalAmount, el.currencyType)}`
                  description = el.meta.description
                }

                if (['TAX_DOCUMENT'].includes(el.documentType)) {

                  if ([0, 1, '0', '1'].includes(el.meta.documentCode)) {
                    documentType = 'Boleta de Honorarios'
                    documentId = `#${el.meta.documentId}`
                    rut = isReceived ? formatRut(el.meta.issuerId) : formatRut(el.meta.receiverId)
                    name = isReceived ? el.meta.issuerName : el.meta.receiverName
                    amount = `${formatMoneyWithCurrency(el.meta.remaining || el.meta.netAmount)}`
                    description = `${rut} ${name}`
                  }

                  if (![0, 1, '0', '1'].includes(el.meta.documentCode)) {
                    documentType = el.meta.summary
                    documentId = `#${el.meta.documentId}`
                    rut = isReceived ? formatRut(el.meta.issuerId) : formatRut(el.meta.receiverId)
                    name = isReceived ? el.meta.issuerName : el.meta.receiverName
                    amount = `${formatMoneyWithCurrency(el.meta.remaining || el.meta.netAmount)}`
                    description = `${rut} ${name}`
                  }

                  if ([39].includes(el.meta.documentCode)) {
                    documentType = el.meta.summary
                    documentId = ''
                    rut = isReceived ? formatRut(el.meta.issuerId) : formatRut(el.meta.receiverId)
                    name = t('cashPosition.totalReceipts')
                    amount = `${formatMoneyWithCurrency(el.meta.remaining || el.meta.netAmount)}`
                    description = `${rut} ${name}`
                  }

                  if ([48].includes(el.meta.documentCode)) {
                    documentType = el.meta.summary
                    documentId = ''
                    rut = isReceived ? formatRut(el.meta.issuerId) : formatRut(el.meta.receiverId)
                    name = t('cashPosition.totalElectronicPayment')
                    amount = `${formatMoneyWithCurrency(el.meta.remaining || el.meta.netAmount)}`
                    description = `${rut} ${name}`
                  }
                }

                if (['CREDIT'].includes(el.documentType)) {
                  documentType = `Cuota ${el.meta.paymentNumber} de ${el.meta.totalPayments}`
                  documentId = formatBanks[el.name]
                  rut = ''
                  name = el.meta.identificator
                  
                  // Calcular cuota completa: usar installments solo si es mayor a 1000 (para evitar valores antiguos incorrectos)
                  // Sino, calcular stock + interest que son los valores correctos
                  const installmentsValue = el.meta.installments || el.meta.installmentCLP
                  const calculatedQuota = (el.meta.stock || 0) + (el.meta.interest || 0)
                  
                  // Prioridad: installments válido > calculado (stock+interest) > documentAmount
                  const cuota = (installmentsValue && installmentsValue > 1000) 
                    ? installmentsValue 
                    : (calculatedQuota > 0 ? calculatedQuota : documentAmount)
                  
                  amount = `${formatMoneyWithCurrency(cuota, el.currencyType)}`
                  description = el.meta.identificator
                }

                // Para documentos de banco
                if (['BANK_TRANSACTION'].includes(el.documentType)) {
                  documentType = el.meta?.typeLabel || 'Movimiento Bancario'
                  documentId = el.meta?.number ? `#${el.meta.number}` : ''
                  rut = ''
                  name = el.meta?.description || el.name
                  amount = `${formatMoneyWithCurrency(el.meta?.remaining || el.meta?.amount || el.meta?.originalAmount, el.currencyType)}`
                  description = el.meta?.description || ''
                }

                return (
                  <div className="d-flex reconcileRow" key={el.id || index}>
                    <div className="d-flex justify-content-between py-3 position-relative reconcileTable">
                      <div className="d-flex w-75 reconcileTableInfo">
                        <span
                          className="text-uppercase font-weight-bold position-absolute w-100"
                          style={{ top }}
                          hidden={index > 0}>
                          {t('common.documents')}
                        </span>
                        <div className="d-flex flex-column align-items-center px-3 reconcileDate" >
                          <div className="text-uppercase reconcileMonth" style={{ width: '60px' }}>
                            {moment(el.documentDate).format("MMM DD")}
                          </div>
                          <div className="font-weight-bold reconcileDay">
                            {moment(el.documentDate).format("YYYY")}
                          </div>
                        </div>
                        <div className="d-flex flex-column position-relative" style={{ maxWidth: "88%", fontSize: "13px" }}>
                          <div className="d-flex">
                            <div className="text-truncate reconcileType reconcileTypeWidth">
                              {documentType}
                            </div>
                            <a
                              style={{ marginLeft: "5px", fontSize: "13px" }}
                              href="javascript:;"
                              onClick={() => handleModalData(el)}
                            >
                              {documentId}
                            </a>
                          </div>
                          <div className={`font-weight-bold text-truncate position-relative ${name ?? "eerrItalic"}`} style={{ 'maxWidth': '100%', fontSize: "12px" }}>
                            {description || t('cashPosition.detailFilters.noDetails')}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-end align-items-center reconcileTableAmount">
                        <span
                          className="text-uppercase font-weight-bold position-absolute"
                          style={{ top }}
                          hidden={index > 0}
                        >
                          Monto
                        </span>
                        <div className="reconcileAmount">{`${isReceived ? '(-' : ''}${amount}${isReceived ? ')' : ''}`}</div>
                      </div>
                    </div>
                  </div>
                )
              }) : (
                <div className="d-flex justify-content-center align-items-center w-100 py-4">
                  <span className="text-muted">No hay documentos asociados</span>
                </div>
              ))
              : fetching
                ? <div className="d-flex justify-content-center align-items-center w-100">
                  <LoadingSpinner
                    height="40px"
                    afterHeight="40px"
                    afterWidth="40px"
                  />
                </div>
                : (<div className="d-flex justify-content-center align-items-center w-100 py-4">
                    <span className="text-muted">No hay documentos que mostrar</span>
                  </div>)
            }
          </div>
        </div>
      </div>

      <DocumentDetailContainer />
    </>
  )
}

export default CashPositionDetail
